import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import type { Campus, School } from "@academia-hub/shared-types";
import { useAuth } from "../auth/use-auth.js";
import { fetchCampuses, fetchSchools } from "./institution-api.js";

const SELECTED_SCHOOL_STORAGE_KEY = "academia-hub-selected-school";
const SELECTED_CAMPUS_STORAGE_KEY = "academia-hub-selected-campus";

function readStored(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Ignore write failures; the in-memory selection still applies for this session.
  }
}

/**
 * Query keys for the header's school/campus lists. Keyed by the signed-in user, so a sign-in as
 * someone else never shows the previous user's schools, and prefixed `["schools"]`/`["campuses"]`
 * so invalidating either prefix still refreshes them.
 */
const institutionQueryKeys = {
  schools: (userId: string | null) => ["schools", userId] as const,
  campuses: (userId: string | null, schoolId: string | null) => ["campuses", userId, schoolId] as const,
};

/**
 * Resolves to the stored choice if it's still a valid option, else the first option — computed
 * directly at render time rather than mirrored into its own `useState`/`useEffect` pair, so
 * "auto-select the first school once the list loads" is a pure derivation, not a cascading
 * render (`react-hooks/set-state-in-effect`).
 */
function resolveEffectiveId(storedId: string | null, options: { id: string }[]): string | null {
  if (storedId && options.some((option) => option.id === storedId)) return storedId;
  return options[0]?.id ?? null;
}

interface InstitutionContextValue {
  schools: School[];
  campuses: Campus[];
  selectedSchoolId: string | null;
  selectedCampusId: string | null;
  setSelectedSchoolId: (id: string) => void;
  setSelectedCampusId: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
}

const InstitutionContext = React.createContext<InstitutionContextValue | null>(null);

export function InstitutionProvider({ children }: { children: React.ReactNode }) {
  const [storedSchoolId, setStoredSchoolId] = React.useState<string | null>(() => readStored(SELECTED_SCHOOL_STORAGE_KEY));
  const [storedCampusId, setStoredCampusId] = React.useState<string | null>(() => readStored(SELECTED_CAMPUS_STORAGE_KEY));

  // UAT (Phase 22): the provider sits above `RequireAuth`, so it mounts while the session is still
  // bootstrapping. Fetching `/schools` then went out with no token, failed, and was never retried —
  // the header lost its school selector until a reload. Only fetch once signed in (and not while a
  // forced password/MFA step blocks every other call); the per-user key refetches after a sign-in.
  const { status, user, restriction } = useAuth();
  const userId = user?.id ?? null;
  const canFetch = status === "authenticated" && userId !== null && restriction === null;

  const schoolsQuery = useQuery({
    queryKey: institutionQueryKeys.schools(userId),
    queryFn: fetchSchools,
    enabled: canFetch,
  });
  const schools = React.useMemo(() => schoolsQuery.data?.data ?? [], [schoolsQuery.data]);
  const selectedSchoolId = resolveEffectiveId(storedSchoolId, schools);

  const campusesQuery = useQuery({
    queryKey: institutionQueryKeys.campuses(userId, selectedSchoolId),
    queryFn: () => fetchCampuses(selectedSchoolId!),
    enabled: canFetch && selectedSchoolId !== null,
  });
  const campuses = React.useMemo(() => campusesQuery.data?.data ?? [], [campusesQuery.data]);
  const selectedCampusId = resolveEffectiveId(storedCampusId, campuses);

  const setSelectedSchoolId = React.useCallback((id: string) => {
    setStoredSchoolId(id);
    writeStored(SELECTED_SCHOOL_STORAGE_KEY, id);
    // Switching schools invalidates whatever campus was selected for the previous one.
    setStoredCampusId(null);
    writeStored(SELECTED_CAMPUS_STORAGE_KEY, null);
  }, []);

  const setSelectedCampusId = React.useCallback((id: string) => {
    setStoredCampusId(id);
    writeStored(SELECTED_CAMPUS_STORAGE_KEY, id);
  }, []);

  const value = React.useMemo<InstitutionContextValue>(
    () => ({
      schools,
      campuses,
      selectedSchoolId,
      selectedCampusId,
      setSelectedSchoolId,
      setSelectedCampusId,
      isLoading: status === "loading" || schoolsQuery.isLoading || campusesQuery.isLoading,
      isError: schoolsQuery.isError || campusesQuery.isError,
    }),
    [
      schools,
      campuses,
      selectedSchoolId,
      selectedCampusId,
      setSelectedSchoolId,
      setSelectedCampusId,
      status,
      schoolsQuery.isLoading,
      schoolsQuery.isError,
      campusesQuery.isLoading,
      campusesQuery.isError,
    ],
  );

  return <InstitutionContext.Provider value={value}>{children}</InstitutionContext.Provider>;
}

export function useInstitution(): InstitutionContextValue {
  const context = React.useContext(InstitutionContext);
  if (!context) throw new Error("useInstitution must be used within an InstitutionProvider");
  return context;
}
